import { createContext, useContext, useState, ReactNode } from 'react'
import { AccountUser } from '../api/client'

interface AuthState {
  token: string | null
  user: AccountUser | null
  setSession: (token: string, user: AccountUser) => void
  logout: () => void
}

const AuthContext = createContext<AuthState | null>(null)

function loadStoredUser(): AccountUser | null {
  const raw = localStorage.getItem('user')
  if (!raw) return null
  try {
    return JSON.parse(raw) as AccountUser
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'))
  const [user, setUser] = useState<AccountUser | null>(loadStoredUser())

  const setSession = (newToken: string, newUser: AccountUser) => {
    localStorage.setItem('token', newToken)
    localStorage.setItem('user', JSON.stringify(newUser))
    setToken(newToken)
    setUser(newUser)
  }

  const logout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    setToken(null)
    setUser(null)
  }

  return <AuthContext.Provider value={{ token, user, setSession, logout }}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
